import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import bannerService from '../../services/bannerService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import { getImageUrl } from '../../utils/imageUrl';
import './BannerSection.css';

// 4.0 seconds per slide: balanced pace that is neither too fast nor too slow
const AUTO_SLIDE_INTERVAL = 4000;

// Matches mobile phones and portrait tablets where mobile-specific banner art is preferred
const MOBILE_BANNER_MEDIA_QUERY =
    '(max-width: 768px) and (orientation: portrait), (max-width: 640px)';

const getInitialMobileMatch = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }
    return window.matchMedia(MOBILE_BANNER_MEDIA_QUERY).matches;
};

const BannerSection = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState('next');
    const [isPaused, setIsPaused] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(getInitialMobileMatch);

    // Natural width/height ratio map keyed by resolved image URL
    const [imageRatios, setImageRatios] = useState({});
    // Track individual failed asset URLs (so broken mobile_image falls back to desktop_image)
    const [failedAssetUrls, setFailedAssetUrls] = useState({});
    // Track banners where all candidate images failed to load
    const [failedBannerIds, setFailedBannerIds] = useState({});

    const timerRef = useRef(null);
    const touchStartXRef = useRef(0);
    const touchCurrentXRef = useRef(0);
    const carouselRef = useRef(null);
    const isInteractingRef = useRef(false);

    // Track viewport breakpoint changes so desktop/mobile banner sources & ratios stay synced
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQueryList = window.matchMedia(MOBILE_BANNER_MEDIA_QUERY);
        const handleViewportChange = (event) => {
            setIsMobileViewport(event.matches);
        };

        setIsMobileViewport(mediaQueryList.matches);

        if (typeof mediaQueryList.addEventListener === 'function') {
            mediaQueryList.addEventListener('change', handleViewportChange);
            return () => mediaQueryList.removeEventListener('change', handleViewportChange);
        }

        mediaQueryList.addListener(handleViewportChange);
        return () => mediaQueryList.removeListener(handleViewportChange);
    }, []);

    // Fetch active banners from API (with silent refresh support for real-time updates)
    const loadBanners = useCallback(async (showLoadingState = true) => {
        try {
            if (showLoadingState) setLoading(true);
            const activeBanners = await bannerService.getActiveBanners();
            setBanners(activeBanners);
            setFailedBannerIds({});
            setCurrentIndex((prev) => {
                if (activeBanners.length === 0) return 0;
                return prev >= activeBanners.length ? 0 : prev;
            });
        } catch (err) {
            console.warn('[BannerSection] Error fetching banners:', err);
            setBanners([]);
        } finally {
            if (showLoadingState) setLoading(false);
        }
    }, []);

    // Initial fetch on mount
    useEffect(() => {
        loadBanners(true);
    }, [loadBanners]);

    // Real-time synchronization: refresh banners without manual page reload
    useRealtimeSync(
        'banners',
        useCallback(() => {
            loadBanners(false);
        }, [loadBanners])
    );

    // Filter out any banners whose image URLs completely failed to load
    const visibleBanners = useMemo(
        () => banners.filter((banner) => banner && !failedBannerIds[banner._id]),
        [banners, failedBannerIds]
    );

    // Resolve which image URL should render for a given banner on the current device
    const resolveActiveBannerSrc = useCallback(
        (banner) => {
            if (!banner) return null;
            const desktopSrc = banner.desktop_image ? getImageUrl(banner.desktop_image) : null;
            const mobileSrc = banner.mobile_image ? getImageUrl(banner.mobile_image) : null;

            if (isMobileViewport && mobileSrc && !failedAssetUrls[mobileSrc]) {
                return mobileSrc;
            }
            if (desktopSrc && !failedAssetUrls[desktopSrc]) {
                return desktopSrc;
            }
            if (mobileSrc && !failedAssetUrls[mobileSrc]) {
                return mobileSrc;
            }
            return null;
        },
        [isMobileViewport, failedAssetUrls]
    );

    // Keep currentIndex within bounds if visibleBanners shrinks
    useEffect(() => {
        if (visibleBanners.length === 0) {
            if (currentIndex !== 0) setCurrentIndex(0);
            return;
        }
        if (currentIndex >= visibleBanners.length) {
            setCurrentIndex(0);
        }
    }, [visibleBanners.length, currentIndex]);

    const safeIndex =
        visibleBanners.length > 0 ? Math.min(currentIndex, visibleBanners.length - 1) : 0;

    // Record natural aspect ratio of an image element once loaded or read from cache
    const recordNaturalRatio = useCallback((src, imgEl) => {
        if (!src || !imgEl) return;
        const { naturalWidth, naturalHeight } = imgEl;
        if (!naturalWidth || !naturalHeight) return;

        const rawRatio = naturalWidth / naturalHeight;
        // Clamp extreme outliers so stage layout always remains balanced
        const clampedRatio = Math.min(Math.max(rawRatio, 0.45), 5.0);

        setImageRatios((prev) => {
            if (prev[src] && Math.abs(prev[src] - clampedRatio) < 0.005) {
                return prev;
            }
            return { ...prev, [src]: clampedRatio };
        });
    }, []);

    // Handle broken image URLs gracefully (fallback mobile -> desktop, or hide broken slide)
    const handleImageError = useCallback((banner, failedSrc) => {
        if (!banner || !failedSrc) return;

        const desktopSrc = banner.desktop_image ? getImageUrl(banner.desktop_image) : null;
        const mobileSrc = banner.mobile_image ? getImageUrl(banner.mobile_image) : null;

        setFailedAssetUrls((prev) => {
            if (prev[failedSrc]) return prev;
            const nextFailed = { ...prev, [failedSrc]: true };

            const desktopStillAvailable = desktopSrc && !nextFailed[desktopSrc];
            const mobileStillAvailable = mobileSrc && !nextFailed[mobileSrc];

            if (!desktopStillAvailable && !mobileStillAvailable) {
                setFailedBannerIds((prevIds) => ({ ...prevIds, [banner._id]: true }));
            }

            return nextFailed;
        });
    }, []);

    // Navigation functions with directional awareness
    const nextSlide = useCallback(() => {
        if (visibleBanners.length <= 1) return;
        setSlideDirection('next');
        setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
    }, [visibleBanners.length]);

    const prevSlide = useCallback(() => {
        if (visibleBanners.length <= 1) return;
        setSlideDirection('prev');
        setCurrentIndex((prev) => (prev - 1 + visibleBanners.length) % visibleBanners.length);
    }, [visibleBanners.length]);

    const goToSlide = useCallback(
        (index) => {
            if (index >= 0 && index < visibleBanners.length) {
                setSlideDirection(index >= safeIndex ? 'next' : 'prev');
                setCurrentIndex(index);
            }
        },
        [visibleBanners.length, safeIndex]
    );

    // Helper to reset the auto-slide timer when user manually interacts
    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (visibleBanners.length > 1 && !isPaused && !isInteractingRef.current) {
            timerRef.current = setInterval(() => {
                nextSlide();
            }, AUTO_SLIDE_INTERVAL);
        }
    }, [visibleBanners.length, isPaused, nextSlide]);

    // Auto-advance timer management
    useEffect(() => {
        if (visibleBanners.length <= 1 || isPaused) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        timerRef.current = setInterval(() => {
            nextSlide();
        }, AUTO_SLIDE_INTERVAL);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visibleBanners.length, isPaused, nextSlide]);

    // Manual navigation wrappers that reset the timer
    const handleManualNext = useCallback(() => {
        nextSlide();
        resetTimer();
    }, [nextSlide, resetTimer]);

    const handleManualPrev = useCallback(() => {
        prevSlide();
        resetTimer();
    }, [prevSlide, resetTimer]);

    const handleManualGoTo = useCallback(
        (index) => {
            goToSlide(index);
            resetTimer();
        },
        [goToSlide, resetTimer]
    );

    // Keyboard navigation (ArrowLeft / ArrowRight)
    const handleKeyDown = (e) => {
        if (visibleBanners.length <= 1) return;
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            handleManualPrev();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            handleManualNext();
        }
    };

    // Smooth Touch Drag & Swipe Gestures for Mobile
    const handleTouchStart = (e) => {
        if (visibleBanners.length <= 1) return;
        isInteractingRef.current = true;
        const x = e.touches[0].clientX;
        touchStartXRef.current = x;
        touchCurrentXRef.current = x;
    };

    const handleTouchMove = (e) => {
        if (visibleBanners.length <= 1) return;
        touchCurrentXRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (visibleBanners.length <= 1) return;
        isInteractingRef.current = false;

        const diff = touchCurrentXRef.current - touchStartXRef.current;
        const minSwipeThreshold = 40; // px

        if (diff < -minSwipeThreshold) {
            handleManualNext();
        } else if (diff > minSwipeThreshold) {
            handleManualPrev();
        } else {
            resetTimer();
        }
    };

    // Compute responsive stage aspect ratio from loaded banner dimensions
    // - Single banner: matches exact natural aspect ratio of active desktop/mobile image
    // - Multi-banner carousel: uses a stable stage ratio across slides to prevent page layout shift
    const stageAspectRatio = useMemo(() => {
        if (visibleBanners.length === 0) return 16 / 9;

        const currentBanner = visibleBanners[safeIndex];
        const currentSrc = resolveActiveBannerSrc(currentBanner);
        const currentRatio = currentSrc ? imageRatios[currentSrc] : null;
        const defaultRatio =
            isMobileViewport && Boolean(currentBanner?.mobile_image) ? 4 / 5 : 16 / 9;

        if (visibleBanners.length === 1) {
            return currentRatio || defaultRatio;
        }

        const knownRatios = visibleBanners
            .map((b) => {
                const src = resolveActiveBannerSrc(b);
                return src ? imageRatios[src] : null;
            })
            .filter((r) => typeof r === 'number' && r > 0);

        if (knownRatios.length === 0) {
            return defaultRatio;
        }

        const minRatio = Math.min(...knownRatios);
        const maxRatio = Math.max(...knownRatios);

        // Keep mixed-ratio carousels balanced on mobile while preserving full uncropped images
        if (isMobileViewport && maxRatio / minRatio > 1.8) {
            return Math.max(minRatio, 0.85);
        }

        return minRatio;
    }, [visibleBanners, safeIndex, resolveActiveBannerSrc, imageRatios, isMobileViewport]);

    // Prevent CLS with skeleton while loading
    if (loading) {
        return (
            <div className="banner-section banner-section--loading" aria-hidden="true">
                <div className="container">
                    <div className="banner-skeleton-frame">
                        <div className="banner-skeleton-shimmer" />
                    </div>
                </div>
            </div>
        );
    }

    // If no active banners returned from backend, render nothing (no dummy banners)
    if (!visibleBanners || visibleBanners.length === 0) {
        return null;
    }

    const isSingle = visibleBanners.length === 1;
    const currentBanner = visibleBanners[safeIndex];
    const currentTargetUrl = currentBanner?.cta_url || '/products';
    const currentIsExternal =
        Boolean(currentBanner?.cta_url) &&
        (currentBanner.cta_url.startsWith('http://') ||
            currentBanner.cta_url.startsWith('https://'));
    const showMobileFooterBar = !isSingle || Boolean(currentBanner?.cta_text);

    return (
        <section
            ref={carouselRef}
            className="banner-section"
            aria-label="Promotional Offers"
            aria-roledescription={isSingle ? 'banner' : 'carousel'}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => {
                setIsPaused(false);
                resetTimer();
            }}
            onFocus={() => setIsPaused(true)}
            onBlur={() => {
                setIsPaused(false);
                resetTimer();
            }}
            onKeyDown={handleKeyDown}
            tabIndex={0}
        >
            <div className="container">
                <div className="banner-carousel-shell">
                    <div
                        className="banner-carousel-wrapper"
                        style={{ '--banner-ratio': stageAspectRatio }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                    >
                        {/* Slides Track: stacked crossfade with smooth directional drift */}
                        <div className="banner-track" data-direction={slideDirection}>
                            {visibleBanners.map((banner, idx) => {
                                const isCurrent = idx === safeIndex;
                                const activeSrc = resolveActiveBannerSrc(banner);
                                if (!activeSrc) return null;

                                const isExternal =
                                    banner.cta_url &&
                                    (banner.cta_url.startsWith('http://') ||
                                        banner.cta_url.startsWith('https://'));
                                const targetUrl = banner.cta_url || '/products';
                                const isCoverMode = banner.image_fit === 'cover';
                                const focalPosition = banner.focal_point || 'center';

                                const slideMedia = (
                                    <>
                                        {/* Harmonious blurred ambient backdrop fills any extra viewport width on wide screens without cropping */}
                                        {!isCoverMode && (
                                            <div className="banner-ambient-layer" aria-hidden="true">
                                                <img
                                                    src={activeSrc}
                                                    alt=""
                                                    className="banner-ambient-bg"
                                                    decoding="async"
                                                    referrerPolicy="no-referrer"
                                                />
                                            </div>
                                        )}

                                        <div className="banner-media-frame">
                                            <img
                                                ref={(imgEl) => {
                                                    if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
                                                        recordNaturalRatio(activeSrc, imgEl);
                                                    }
                                                }}
                                                src={activeSrc}
                                                alt={
                                                    banner.alt_text ||
                                                    banner.title ||
                                                    'Rahber Corporation Promotional Offer'
                                                }
                                                className={`banner-img ${isCoverMode ? 'banner-img--cover' : 'banner-img--contain'}`}
                                                style={{ objectPosition: focalPosition }}
                                                loading={idx === 0 ? 'eager' : 'lazy'}
                                                fetchpriority={idx === 0 ? 'high' : 'auto'}
                                                decoding="async"
                                                referrerPolicy="no-referrer"
                                                onLoad={(e) => recordNaturalRatio(activeSrc, e.currentTarget)}
                                                onError={() => handleImageError(banner, activeSrc)}
                                            />
                                        </div>

                                        {/* Accessible semantic heading for screen readers & SEO */}
                                        <h2 className="sr-only">
                                            {banner.title}
                                            {banner.subtitle ? ` — ${banner.subtitle}` : ''}
                                        </h2>

                                        {/* Desktop/Tablet Floating CTA Pill if CTA text is specified */}
                                        {banner.cta_text && (
                                            <div className="banner-floating-cta" aria-hidden="true">
                                                <span>{banner.cta_text}</span>
                                                <FiArrowRight className="banner-floating-cta-icon" />
                                            </div>
                                        )}
                                    </>
                                );

                                return (
                                    <div
                                        key={banner._id || idx}
                                        className={`banner-slide ${isCurrent ? 'banner-slide--active' : ''}`}
                                        aria-hidden={!isCurrent}
                                        role="group"
                                        aria-roledescription="slide"
                                        aria-label={`${idx + 1} of ${visibleBanners.length}: ${banner.title || 'Promotional Banner'}`}
                                    >
                                        {isExternal ? (
                                            <a
                                                href={targetUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="banner-slide-link"
                                                title={banner.title || 'View Promotion'}
                                                tabIndex={isCurrent ? 0 : -1}
                                            >
                                                {slideMedia}
                                            </a>
                                        ) : (
                                            <Link
                                                to={targetUrl}
                                                className="banner-slide-link"
                                                title={banner.title || 'View Promotion'}
                                                tabIndex={isCurrent ? 0 : -1}
                                            >
                                                {slideMedia}
                                            </Link>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Navigation Arrows (Only rendered if multiple banners exist) */}
                        {!isSingle && (
                            <>
                                <button
                                    type="button"
                                    className="banner-nav-btn banner-nav-btn--prev"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleManualPrev();
                                    }}
                                    aria-label="Previous promotional banner"
                                    title="Previous banner (Left arrow)"
                                >
                                    <FiChevronLeft />
                                </button>
                                <button
                                    type="button"
                                    className="banner-nav-btn banner-nav-btn--next"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleManualNext();
                                    }}
                                    aria-label="Next promotional banner"
                                    title="Next banner (Right arrow)"
                                >
                                    <FiChevronRight />
                                </button>
                            </>
                        )}

                        {/* Desktop/Tablet Carousel Controls: Dot Indicators & Counter */}
                        {!isSingle && (
                            <div className="banner-controls-bar">
                                <span className="banner-counter" aria-hidden="true">
                                    <span className="banner-counter-current">
                                        {String(safeIndex + 1).padStart(2, '0')}
                                    </span>
                                    <span className="banner-counter-sep">/</span>
                                    <span className="banner-counter-total">
                                        {String(visibleBanners.length).padStart(2, '0')}
                                    </span>
                                </span>

                                <div className="banner-dots" role="tablist" aria-label="Banner slides">
                                    {visibleBanners.map((b, idx) => (
                                        <button
                                            key={b._id || idx}
                                            type="button"
                                            role="tab"
                                            aria-selected={safeIndex === idx}
                                            aria-label={`Go to banner ${idx + 1}`}
                                            className={`banner-dot ${safeIndex === idx ? 'banner-dot--active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleManualGoTo(idx);
                                            }}
                                        >
                                            <span className="banner-dot-progress" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Dedicated Mobile Control & Action Strip (<= 640px) so zero badges cover small mobile banner text */}
                    {showMobileFooterBar && (
                        <div className="banner-mobile-bar">
                            {!isSingle ? (
                                <div className="banner-mobile-bar__controls">
                                    <span className="banner-counter" aria-hidden="true">
                                        <span className="banner-counter-current">
                                            {String(safeIndex + 1).padStart(2, '0')}
                                        </span>
                                        <span className="banner-counter-sep">/</span>
                                        <span className="banner-counter-total">
                                            {String(visibleBanners.length).padStart(2, '0')}
                                        </span>
                                    </span>

                                    <div
                                        className="banner-dots"
                                        role="tablist"
                                        aria-label="Mobile banner slides"
                                    >
                                        {visibleBanners.map((b, idx) => (
                                            <button
                                                key={b._id || idx}
                                                type="button"
                                                role="tab"
                                                aria-selected={safeIndex === idx}
                                                aria-label={`Go to banner ${idx + 1}`}
                                                className={`banner-dot ${safeIndex === idx ? 'banner-dot--active' : ''}`}
                                                onClick={() => handleManualGoTo(idx)}
                                            >
                                                <span className="banner-dot-progress" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <span className="banner-mobile-bar__title">
                                    {currentBanner?.title || 'Special Promotion'}
                                </span>
                            )}

                            {currentBanner?.cta_text &&
                                (currentIsExternal ? (
                                    <a
                                        href={currentTargetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="banner-mobile-bar__cta"
                                    >
                                        <span>{currentBanner.cta_text}</span>
                                        <FiArrowRight className="banner-floating-cta-icon" />
                                    </a>
                                ) : (
                                    <Link
                                        to={currentTargetUrl}
                                        className="banner-mobile-bar__cta"
                                    >
                                        <span>{currentBanner.cta_text}</span>
                                        <FiArrowRight className="banner-floating-cta-icon" />
                                    </Link>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default BannerSection;
